import { MUICheckBox, MUIChip } from "@abb/arcadia-ui-react"
import { MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useRoleRequests } from "../../../../hooks/useRoleRequests"
import { useRoleRequestsBadge } from "../../../../hooks/useRoleRequestsBadge"
import { AppContext } from "../../../context/AppContext"
import { RoleRequestGet, RoleRequestStatusEnum } from "../../../models/RoleRequest"
import {
  dateStringComparator,
  getNiceFormattedDate,
} from "../../../utils/getFormattedDate"
import { getRoleRequestStatusProps } from "../../../utils/getRoleRequestStatusProps"
import { EntityRow, EntityTab } from "../../partials/EntityTab"

export function RoleRequests() {
  const { badges, setBadges } = useContext(AppContext)
  const [getCompleted, setGetCompleted] = useState<boolean>(false)
  const { roleRequestsList, isLoadingRoleRequests } = useRoleRequests(getCompleted)
  const { roleRequestsBadge } = useRoleRequestsBadge()

  useEffect(() => {
    if (roleRequestsBadge !== undefined && badges.roleRequests !== roleRequestsBadge) {
      setBadges({
        ...badges,
        roleRequests: roleRequestsBadge,
      })
    }
  }, [badges, setBadges, roleRequestsBadge])

  const cols = [
    {
      field: "creationDate",
      headerName: "Creation date",
      isLink: true,
      linkBasePath: "/role-requests",
      renderCell: (row: EntityRow) => getNiceFormattedDate(String(row.creationDate)),
      sortComparator: dateStringComparator,
    },
    { field: "username", headerName: "Requestor" },
    {
      field: "roleName",
      headerName: "Role",
      renderCell: (row: EntityRow) => `${row.applicationShortName} - ${row.roleName}`,
    },
    {
      field: "status",
      headerName: "Status",
      renderCell: (row: EntityRow) => (
        <MUIChip
          {...getRoleRequestStatusProps((row.status ?? "") as RoleRequestStatusEnum)}
        />
      ),
    },
  ]

  const rows = roleRequestsList.map((roleRequest: RoleRequestGet) => ({
    id: roleRequest.id,
    username: roleRequest.user.username,
    roleName: roleRequest.role.name,
    applicationShortName: roleRequest.application.shortName,
    status: roleRequest.status,
    creationDate: roleRequest.creationDate,
  }))

  const onChange = useCallback(() => {
    setGetCompleted((completed) => !completed)
  }, [])

  const handleIncludeCompletedToggle = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      onChange()
    },
    [onChange]
  )

  const toolbar = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          width: "100%",
        }}
      >
        <MUICheckBox
          checked={getCompleted}
          label="Include completed requests"
          size={16}
          handleToggle={handleIncludeCompletedToggle}
        />
      </div>
    ),
    [getCompleted, handleIncludeCompletedToggle]
  )

  return (
    <EntityTab
      rows={rows}
      cols={cols}
      isLoading={isLoadingRoleRequests}
      toolbar={toolbar}
    />
  )
}
